// Periodos de Comisiones pagadas. "Esta semana" es el corte en curso; los demás suman los cortes
// cerrados cuya semana toca el periodo más el corte en curso. Los cortes van de lunes a domingo,
// como cierra el motor.
export type PayoutPeriod = 'semana' | 'mes' | '3m' | '6m' | 'anio' | '52s';

export const DEFAULT_PAYOUT_PERIOD: PayoutPeriod = 'mes';

export const PAYOUT_PERIOD_OPTIONS: ReadonlyArray<{
    value: PayoutPeriod;
    label: string;
    inText: string;
}> = [
    { value: 'semana', label: 'Esta semana', inText: 'esta semana' },
    { value: 'mes', label: 'Este mes', inText: 'este mes' },
    { value: '3m', label: 'Últimos 3 meses', inText: 'últimos 3 meses' },
    { value: '6m', label: 'Últimos 6 meses', inText: 'últimos 6 meses' },
    { value: 'anio', label: 'Este año', inText: 'este año' },
    { value: '52s', label: 'Últimas 52 semanas', inText: 'últimas 52 semanas' },
];

export function parsePayoutPeriod(raw: string | undefined): PayoutPeriod {
    return (
        PAYOUT_PERIOD_OPTIONS.find((option) => option.value === raw)?.value ?? DEFAULT_PAYOUT_PERIOD
    );
}

/** Semanas de historial que puede mostrar una ficha. */
export const WEEK_WINDOWS = [12, 26, 52] as const;
export type WeeksWindow = (typeof WEEK_WINDOWS)[number];

/** El periodo en minúsculas, para subtítulos: "este mes". */
export function periodText(period: PayoutPeriod): string {
    return PAYOUT_PERIOD_OPTIONS.find((option) => option.value === period)?.inText ?? '';
}

const CUT_ZONE = 'America/Mexico_City';

/** Hoy en la zona del corte, `YYYY-MM-DD`. */
export function localToday(now: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: CUT_ZONE }).format(now);
}

const DAY_MS = 86_400_000;
const atNoon = (day: string) => new Date(`${day}T12:00:00Z`);
const dayOf = (date: Date) => date.toISOString().slice(0, 10);

export function addDays(day: string, days: number): string {
    return dayOf(new Date(atNoon(day).getTime() + days * DAY_MS));
}

/** El lunes de la semana del día: es el `periodId` de su corte. */
export function weekStart(day: string): string {
    return addDays(day, -((atNoon(day).getUTCDay() + 6) % 7));
}

/** El mismo día unos meses antes; si ese mes es más corto, su último día (31 may − 3 meses = 28 feb). */
function monthsBack(day: string, months: number): string {
    const [year, month, date] = day.split('-').map(Number);
    const target = month - 1 - months;
    // El día 0 del mes siguiente es el último del mes destino.
    const lastDay = new Date(Date.UTC(year, target + 1, 0, 12)).getUTCDate();
    return dayOf(new Date(Date.UTC(year, target, Math.min(date, lastDay), 12)));
}

/** El motor suma hasta 53 cortes por consulta. */
const MAX_PERIODS = 53;

/** Del corte de la semana donde empieza el periodo al de la semana en curso; `null` en "Esta semana". */
export function periodRange(
    period: PayoutPeriod,
    today: string,
): { from: string; to: string } | null {
    const from = periodStart(period, today);
    if (from === null) return null;
    const to = weekStart(today);
    const earliest = addDays(to, -(MAX_PERIODS - 1) * 7);
    return { from: from < earliest ? earliest : from, to };
}

function periodStart(period: PayoutPeriod, today: string): string | null {
    switch (period) {
        case 'semana':
            return null;
        case 'mes':
            return weekStart(`${today.slice(0, 7)}-01`);
        case '3m':
            return weekStart(monthsBack(today, 3));
        case '6m':
            return weekStart(monthsBack(today, 6));
        case 'anio':
            return weekStart(`${today.slice(0, 4)}-01-01`);
        case '52s':
            return addDays(weekStart(today), -51 * 7);
    }
}

const SHORT_DAY = new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
});

/** "28 sep – 4 oct": la semana del corte. */
export function weekLabel(periodId: string): string {
    return `${SHORT_DAY.format(atNoon(periodId))} – ${SHORT_DAY.format(atNoon(addDays(periodId, 6)))}`;
}
